<template>
  <!-- The async-setup screen is swapped in at the root once the org probe
       resolves; a stable root element keeps the route <Suspense> anchor
       valid (see .stable-route-root in global.scss). -->
  <div class="stable-route-root">
    <div v-if="entitlementBlocked" />
    <Agent
      v-else-if="assistantId && organizationId"
      :key="`${organizationId}:${assistantId}`"
      :organization-id="organizationId"
      :agent-id="assistantId"
    />
  </div>
</template>

<script lang="ts">
import { computedAsync } from "@vueuse/core";
import { computed, defineComponent, ref, watch } from "vue";

import type { AssistantId, OrgId } from "@ctrl/controller-rpc";

import Agent from "@/components/ai/Agent.vue";
import * as inject from "@/components/inject";
import { openUpgradeModal } from "@/components/modals/upgrade";
import { waitForEntitlement } from "@/utils/billing";

const ENTITLEMENT_KEY = "ai-assistant";

export default defineComponent({
  name: "AssistantRoute",
  components: { Agent },
  setup() {
    const controller = inject.controller();
    const billing = inject.billing();
    const profile = inject.profile();

    const assistantId = computedAsync<AssistantId | null>(async () => {
      const assistantRef = await controller.ai.assistant.get();
      const id = assistantRef.value;
      assistantRef.release();
      return id;
    }, null);

    const organizationId = computed<OrgId | null>(
      () => profile.value.user.personal.organizationId satisfies OrgId | null
    );
    const entitlementBlocked = ref(false);

    watch(
      () => [assistantId.value, organizationId.value, billing.value] as const,
      async ([, org]) => {
        if (!org) return;
        // Account-level: any org's plan grants the assistant (it lives in
        // the personal org, which stays Free when a team org is on Business).
        // Waits for the entitlement to be known; still-unknown fails open
        // (see waitForEntitlement).
        const entitled = await waitForEntitlement(billing, ENTITLEMENT_KEY);
        if (!entitled) {
          openUpgradeModal(billing, undefined, ENTITLEMENT_KEY);
          entitlementBlocked.value = true;
        } else {
          entitlementBlocked.value = false;
        }
      },
      { immediate: true }
    );

    return { assistantId, organizationId, entitlementBlocked };
  },
});
</script>
